class WishlistObjectQf {
  constructor(container = document) {
    // The container defines the scope of this instance (grid/buttons)
    this.container = container;

    // Local storage keys
    this.LOCAL_STORAGE_CLIENT_NAME = window.Shopify.shop.replace(
      /.myshopifay.com/g,
      ''
    );
    this.LOCAL_STORAGE_WISHLIST_KEY =
      this.LOCAL_STORAGE_CLIENT_NAME + '-wishlist';

    // Classes & selectors
    this.BUTTON_ACTIVE_CLASS = 'active-wishlist';
    this.GRID_LOADED_CLASS = 'loaded';
    this.PRODUCT_CARD = 'product-card-nocarousel';

    this.selectors = {
      button: '[button-wishlist]',
      clearWishlist: '.clear-wishlist',
      grid: '[grid-wishlist]',
      gridContainer: '.wishlist-grid-container',
      sideCartGrid: '[side-cart-grid-wishlist]',
      productCard: '.product-card-nocarousel',
      wishlistCount: '.wishlistCount',
      emptyWishlist: '.empty-wishlist',
    };

    // Only bind global events once
    if (!WishlistObjectQf.globalListenersBound) {
      this.bindGlobalListeners();
      WishlistObjectQf.globalListenersBound = true;
    }

    // Initialize this container's DOM
    this.init();
  }

  // Initialize DOM elements within this container
  init() {
    this.updateWishlistCount();
    this.updateButtons();
    this.initGrids();
  }

  // -------------------------------
  // GLOBAL EVENT LISTENERS (only bound once)
  // -------------------------------
  bindGlobalListeners() {
    document.body.addEventListener('click', (e) => {
      const button = e.target.closest(this.selectors.button);
      if (button) {
        e.preventDefault();
        const product = this.getProductFromButton(button);
        if (!product.handle) {
          return console.error(
            '[Shopify Wishlist] Missing `data-product-handle` attribute.'
          );
        }
        this.toggleWishlistItem(product);
      }

      const clearButton = e.target.closest(this.selectors.clearWishlist);
      if (clearButton) {
        this.resetWishlist();
      }
    });

    window.addEventListener('desktop-cart-recommendations-rendered', () => {
      document.querySelectorAll('[button-wishlist]').forEach((button) => {
        const handle = button.dataset.productHandle;
        if (this.wishlistContains(handle)) {
          button.classList.add(this.BUTTON_ACTIVE_CLASS);
        }
      });
    });
  }

  // -------------------------------
  // WISHLIST MANAGEMENT
  // -------------------------------
  getWishlist() {
    const wishlist =
      localStorage.getItem(this.LOCAL_STORAGE_WISHLIST_KEY) || '[]';
    return JSON.parse(wishlist);
  }

  setWishlist(array) {
    if (array.length) {
      localStorage.setItem(
        this.LOCAL_STORAGE_WISHLIST_KEY,
        JSON.stringify(array)
      );
    } else {
      localStorage.removeItem(this.LOCAL_STORAGE_WISHLIST_KEY);
    }

    // Update all grids and counts on the page
    document.querySelectorAll('[grid-wishlist]').forEach((grid) => {
      this.initGrid(grid);
    });
    document.querySelectorAll(this.selectors.wishlistCount).forEach((el) => {
      const count = this.getWishlist().length;
      el.textContent = count;
      el.classList.toggle('is-hidden', count === 0);
    });
  }

  toggleWishlistItem(product) {
    const wishlist = this.getWishlist();
    const index = wishlist.findIndex((i) => i.handle === product.handle);
    if (index === -1) {
      wishlist.push(product);
    } else {
      wishlist.splice(index, 1);
    }
    this.setWishlist(wishlist);

    // Toggle button active state globally
    document.querySelectorAll(
      `${this.selectors.button}[data-product-handle="${product.handle}"]`
    ).forEach((btn) => btn.classList.toggle(this.BUTTON_ACTIVE_CLASS));
  }

  wishlistContains(handle) {
    return this.getWishlist().some((item) => item.handle === handle);
  }

  resetWishlist() {
    document
      .querySelectorAll(this.selectors.button)
      .forEach((btn) => btn.classList.remove(this.BUTTON_ACTIVE_CLASS));
    this.setWishlist([]);
  }

  // -------------------------------
  // BUTTONS & PRODUCT HELPERS
  // -------------------------------
  getProductFromButton(button) {
    const attributes = {};
    this.container.querySelectorAll("input[type='radio']").forEach((radio) => {
      if (radio.checked) attributes[radio.name] = radio.value;
    });
    return {
      handle: button.dataset.productHandle || false,
      attributes,
    };
  }

  updateButtons() {
    this.container.querySelectorAll(this.selectors.button).forEach((button) => {
      const handle = button.dataset.productHandle;
      if (this.wishlistContains(handle)) {
        button.classList.add(this.BUTTON_ACTIVE_CLASS);
      } else {
        button.classList.remove(this.BUTTON_ACTIVE_CLASS);
      }
    });
  }

  updateWishlistCount() {
    const count = this.getWishlist().length;
    this.container.querySelectorAll(this.selectors.wishlistCount).forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-hidden', count === 0);
    });
  }

  // -------------------------------
  // GRID INITIALIZATION
  // -------------------------------
  initGrids() {
    this.container.querySelectorAll(this.selectors.grid).forEach((grid) => {
      this.initGrid(grid);
    });
  }

  async initGrid(grid) {
    const wishlist = this.getWishlist();
    const gridContainer = grid.closest(this.selectors.gridContainer);
    const fallback = gridContainer
      ? gridContainer.querySelector(this.selectors.emptyWishlist)
      : null;

    if (!wishlist.length) {
      grid.innerHTML = '';
      grid.classList.add(this.GRID_LOADED_CLASS);
      if (fallback) fallback.classList.add('show');
      return;
    } else if (fallback) {
      fallback.classList.remove('show');
    }

    grid.classList.remove(this.GRID_LOADED_CLASS);

    const handles = wishlist.map((item) => item.handle);
    const productsHTML = await Promise.all(
      handles.map(async (handle) => {
        try {
          const url =
            window.Shopify.routes.root +
            `products/${handle}?view=card&section_id=${this.PRODUCT_CARD}`;
          const res = await fetch(url);
          if (!res.ok) throw new Error(res.status);
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const card = doc.querySelector(this.selectors.productCard);
          if (card) {
            const btn = card.querySelector('.button-wishlist');
            if (btn) btn.classList.add(this.BUTTON_ACTIVE_CLASS);
            return card.outerHTML;
          }
          return null;
        } catch (err) {
          console.error(err);
          return null;
        }
      })
    );

    grid.innerHTML = productsHTML.filter(Boolean).join('');
    grid.classList.add(this.GRID_LOADED_CLASS);
  }
}

// -------------------------------
// SINGLETON INSTANCE
// -------------------------------
if (!window.wishlist) {
  window.wishlist = new WishlistObjectQf();
}
